import React, { useCallback, useEffect, useState } from "react";
import { SingleNote, SingleNoteContent } from "./styles";
import { useTranslation } from "react-i18next";
import { Divider, Grid, Typography, useTheme } from "@mui/material";
import { AlertLineReportNoteDTO } from "../../models/alert-line-report-note/alert-line-report-note-dto";
import { intlFormatDateShort, intlFormatDateToHour } from "../../utils/time-utils";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import { AlertLineReportDTO } from "../../models/alert-line-report/alert-line-report-dto";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import AttachedDocuments from "../AttachedDocuments/AttachedDocuments";
import { getDownloadedFile } from "../../services/documents";

interface Props {
  note: AlertLineReportNoteDTO;
  alertLineReport: AlertLineReportDTO;
  handleFileToView: (file: IFile, noteId: string) => void;
  forExport?: boolean;
}

const AlertLineReportSingleNote = ({ note, alertLineReport, handleFileToView, forExport }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.alert_line_notes";

  const { currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const [audioURL, setAudioURL] = useState<string>("");

  const getAudioURL = useCallback(async () => {
    if (note.vocalMessage && !!note.vocalMessage.files[0]) {
      const file = note.vocalMessage.files[0];
      const res = await getDownloadedFile(file, true);

      if (res?.data) {
        setAudioURL(res?.data ? URL.createObjectURL(res?.data) : "");
      }
    }
  }, [note.vocalMessage]);

  useEffect(() => {
    getAudioURL();
  }, [note.vocalMessage]);

  return (
    <SingleNote>
      <Typography>
        <span>
          {note.asReporter && (!!alertLineReport?.fullName ? alertLineReport?.fullName : `${t(`${tRP}.anonymous`)}`)}
        </span>

        <span>{!note.asReporter && note.asHR && `${t(`${tRP}.hr`)}`}</span>
        {((!note.asReporter && note.asHR && note.asManager) ||
          (!note.asReporter && note.asHR && !note.asManager && note.asCompanyCommitteeMember)) && (
          <Divider
            orientation="vertical"
            flexItem
          />
        )}
        <span>{!note.asReporter && note.asManager && `${t(`${tRP}.manager`)}`}</span>
        {!note.asReporter && note.asManager && note.asCompanyCommitteeMember && (
          <Divider
            orientation="vertical"
            flexItem
          />
        )}
        <span>{!note.asReporter && note.asCompanyCommitteeMember && `${t(`${tRP}.company_committee_member`)}`}</span>
        {currentUserData?.user?.id === note.createdByUser?.id && !forExport && <span>({`${t(`${tRP}.you`)}`})</span>}
        <span style={{ fontSize: "10px" }}>
          &nbsp; {intlFormatDateShort(note.createdAt)} &nbsp;
          {intlFormatDateToHour(note.createdAt)}
        </span>
      </Typography>

      <SingleNoteContent>
        {note.vocalMessage &&
          (forExport ? (
            <Typography variant="body2">{`${t(`${tRP}.recorded_voice_message`)}`}</Typography>
          ) : (
            <Grid>
              <audio
                controls
                src={audioURL}
                preload="auto"
              />
            </Grid>
          ))}

        {!!note.document && (
          <>
            {!forExport && (
              <AttachedDocuments
                documentIDs={[note.document.id]}
                count={note.document.filesCount}
                buttonSize="2x"
                isGenericDocument
                isWidget
                documentIsReadOnly
                allowShare={false}
                allowDownloadContent
                useSimpleView
              />
            )}

            {forExport && <Typography variant="body2">{`${t(`${tRP}.uploaded_file`)}`}</Typography>}
          </>
        )}
        {note.message}
      </SingleNoteContent>
    </SingleNote>
  );
};
export default AlertLineReportSingleNote;
